import React from "react";

import { useTranslation } from "utils";

import { AlertBanner, Drawer, Icon, Typography } from "components";

import { RESILIENCE_TIER_KEYS } from "pages/app/scenario-analysis/utils/resilienceScorecard";

export type ResilienceScoreDrawerProps = {
  visible: boolean;
  onClose: () => void;
  dataTestId?: string;
};

type DriverKey = "tillage" | "residueAndCover" | "diversity";
const DRIVERS: { key: DriverKey; iconName: string; points: string[] }[] = [
  { key: "tillage", iconName: "klim/field-apply", points: ["noTill", "reduced", "plough"] },
  { key: "residueAndCover", iconName: "klim/crops-branching", points: ["residue", "coverCrop", "incorporate"] },
  { key: "diversity", iconName: "klim/crop-rotation", points: ["moreUniqueCrops", "legumes", "mixSeasons"] },
];

export const ResilienceScoreDrawer = ({ visible, onClose, dataTestId = "resilience-score-drawer" }: ResilienceScoreDrawerProps) => {
  const { t } = useTranslation("scenarioAnalysis");

  return (
    <Drawer visible={visible} onClose={onClose} title={t("resilienceScorecard.drawer.title")} titleVariant="h2" dataTestId={dataTestId}>
      <div className="resilience-score-drawer">
        <Typography variant="paragraph-m" color="grey-600" className="resilience-score-drawer__description">
          {t("resilienceScorecard.drawer.description")}
        </Typography>

        <Typography variant="h5" className="resilience-score-drawer__section-title">
          {t("resilienceScorecard.drawer.scoreMeaningTitle")}
        </Typography>
        <div className="resilience-score-drawer__tiers" data-testid={`${dataTestId}-tiers`}>
          {RESILIENCE_TIER_KEYS.map((key) => (
            <div key={key} className="resilience-score-drawer__tier-row" data-testid={`${dataTestId}-tier-${key}`}>
              <div className="resilience-score-drawer__tier-head">
                <Typography variant="paragraph-m" bold className="resilience-score-drawer__tier-range">
                  {t(`resilienceScorecard.drawer.tier.${key}.range`)}
                </Typography>
                <Typography variant="paragraph-xs" bold className={`resilience-score-drawer__tier-chip resilience-score-drawer__tier-chip--${key}`}>
                  {t(`resilienceScorecard.tier.${key}.label`)}
                </Typography>
              </div>
              <Typography variant="paragraph-m" color="grey-600" className="resilience-score-drawer__tier-description">
                {t(`resilienceScorecard.drawer.tier.${key}.description`)}
              </Typography>
            </div>
          ))}
        </div>

        <Typography variant="h5" className="resilience-score-drawer__section-title">
          {t("resilienceScorecard.drawer.driversTitle")}
        </Typography>
        <div className="resilience-score-drawer__drivers">
          {DRIVERS.map((driver) => (
            <div key={driver.key} className="resilience-score-drawer__driver-card" data-testid={`${dataTestId}-driver-${driver.key}`}>
              <div className="resilience-score-drawer__driver-head">
                <span className="resilience-score-drawer__driver-icon" aria-hidden>
                  <Icon iconName={driver.iconName} size="m-light" />
                </span>
                <div className="resilience-score-drawer__driver-title-row">
                  <Typography variant="h6">{t(`resilienceScorecard.drawer.drivers.${driver.key}.title`)}</Typography>
                  <Typography variant="paragraph-xs" bold className="resilience-score-drawer__driver-badge">
                    {t(`resilienceScorecard.drawer.drivers.${driver.key}.badge`)}
                  </Typography>
                </div>
              </div>
              <Typography variant="paragraph-s" color="grey-600" className="resilience-score-drawer__driver-description">
                {t(`resilienceScorecard.drawer.drivers.${driver.key}.description`)}
              </Typography>
              <ul className="resilience-score-drawer__driver-points">
                {driver.points.map((point) => (
                  <li key={point} className="resilience-score-drawer__driver-point">
                    <Icon iconName="tabler/circle-check" size="s" className="resilience-score-drawer__driver-point-icon" />
                    <Typography variant="paragraph-s">{t(`resilienceScorecard.drawer.drivers.${driver.key}.points.${point}`)}</Typography>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <AlertBanner
          className="resilience-score-drawer__insight"
          type="#f1f6ff"
          iconName="tabler/bulb"
          iconSize={24}
          iconAlign="start"
          description={t("resilienceScorecard.drawer.closingInsight")}
          descriptionSize="paragraph-m"
          dataTestId={`${dataTestId}-insight`}
        />
      </div>
    </Drawer>
  );
};

export default ResilienceScoreDrawer;
